import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMyPortfolio, createCustomer, bulkImportCustomers, getUsers, deleteCustomer, deleteAllCustomers } from '@/api/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Upload, Search, Download, Trash2, Phone, Mail, MapPin, User } from 'lucide-react';
import * as XLSX from 'xlsx';
import { useActionHistory } from '@/context/ActionHistoryContext';

const Portfolio = ({ user }) => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCity, setSelectedCity] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('name-asc');
  const { pushAction } = useActionHistory();
  const itemsPerPage = 100;

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    city: '',
    district: '',
    contact_person: '',
    representative: user?.username || ''
  });

  useEffect(() => {
    loadCustomers();
    loadUsers();
  }, []);

  // Benzersiz şehir listesi
  const uniqueCities = useMemo(() => {
    const cities = customers
      .map(c => c.city)
      .filter(city => city && city.trim() !== '');
    return [...new Set(cities)].sort((a, b) => a.localeCompare(b, 'tr'));
  }, [customers]);

  // useMemo ile filtreleme ve sıralamayı optimize et
  const filteredCustomers = useMemo(() => {
    let filtered = customers.filter(customer => {
      const query = searchQuery.toLowerCase();
      const matchesSearch = (
        customer.name?.toLowerCase().startsWith(query) ||
        customer.city?.toLowerCase().startsWith(query) ||
        customer.district?.toLowerCase().startsWith(query) ||
        customer.representative?.toLowerCase().startsWith(query) ||
        customer.phone?.startsWith(searchQuery) ||
        customer.contact_person?.toLowerCase().startsWith(query) ||
        customer.address?.toLowerCase().startsWith(query)
      );
      const matchesCity = selectedCity === 'all' || customer.city === selectedCity;
      
      return matchesSearch && matchesCity;
    });
    
    return filtered.sort((a, b) => {
      switch(sortBy) {
        case 'name-asc':
          return a.name.localeCompare(b.name, 'tr');
        case 'name-desc':
          return b.name.localeCompare(a.name, 'tr');
        case 'city-asc':
          return (a.city || '').localeCompare(b.city || '', 'tr');
        case 'city-desc':
          return (b.city || '').localeCompare(a.city || '', 'tr');
        case 'arvento-desc':
          return (b.arvento_count || 0) - (a.arvento_count || 0);
        case 'arvento-asc':
          return (a.arvento_count || 0) - (b.arvento_count || 0);
        case 'representative-asc':
          return (a.representative || '').localeCompare(b.representative || '', 'tr');
        case 'representative-desc':
          return (b.representative || '').localeCompare(a.representative || '', 'tr');
        default:
          return 0;
      }
    });
  }, [customers, searchQuery, sortBy, selectedCity]);

  // Reset to page 1 when search query or sort changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, sortBy, selectedCity]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentPage]);

  const loadCustomers = async () => {
    try {
      const data = await getMyPortfolio();
      setCustomers(data);
    } catch (error) {
      toast.error('Müşteriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const data = await getUsers();
      setUsers(data);
    } catch (error) {
      console.error('Users load error:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await createCustomer(formData);
      toast.success('Müşteri başarıyla eklendi');
      setShowAddDialog(false);
      loadCustomers();
      setFormData({
        name: '',
        phone: '',
        email: '',
        city: '',
        district: '',
        contact_person: '',
        representative: user?.username || ''
      });
    } catch (error) {
      toast.error('Müşteri eklenirken hata oluştu');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const result = await bulkImportCustomers(file);
      toast.success(`${result.imported_count} müşteri başarıyla içe aktarıldı`);
      setShowImportDialog(false);
      loadCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Dosya yüklenirken hata oluştu');
    }
  };

  const handleExport = () => {
    // Filtre kontrolü - herhangi bir filtre uygulanmış mı?
    const isFiltered = searchQuery.trim() !== '' || 
                       selectedCity !== 'all' ||
                       sortBy !== 'name-asc';
    
    // Filtre varsa filtrelenmiş veriyi, yoksa tüm veriyi kullan
    const dataToExport = isFiltered ? filteredCustomers : customers;
    
    const exportData = dataToExport.map(customer => ({
      'Müşteri Adı': customer.name,
      'Yetkili Kişi': customer.contact_person || '',
      'Telefon': customer.phone || '',
      'E-posta': customer.email || '',
      'Şehir': customer.city || '',
      'İlçe': customer.district || '',
      'Temsilci': customer.representative || '',
      'Arvento Sayısı': customer.arvento_count || 0
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Portföyüm');
    
    const fileName = isFiltered 
      ? `portfoyum_filtreli_${exportData.length}_adet.xlsx`
      : `tum_portfoyum_${exportData.length}_adet.xlsx`;
    
    XLSX.writeFile(wb, fileName);
    toast.success(`${exportData.length} müşteri Excel'e aktarıldı${isFiltered ? ' (filtrelenmiş)' : ' (tümü)'}`);
  };

  const handleDeleteCustomer = async (customerId) => {
    if (!window.confirm('Bu müşteriyi silmek istediğinizden emin misiniz?')) return;
    
    try {
      await deleteCustomer(customerId);
      toast.success('Müşteri silindi');
      loadCustomers();
    } catch (error) {
      toast.error('Müşteri silinirken hata oluştu');
    }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm('TÜM müşterileri silmek istediğinizden emin misiniz? Bu işlem geri alınamaz!')) return;
    if (!window.confirm('GERÇEKTEN tüm müşterileri silmek istiyor musunuz?')) return;
    
    try {
      await deleteAllCustomers();
      toast.success('Tüm müşteriler silindi');
      loadCustomers();
    } catch (error) {
      toast.error('Müşteriler silinirken hata oluştu');
    }
  };

  const paginatedCustomers = filteredCustomers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-lg">Portföyünüz yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header Section - Mobil responsive */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Portföyüm</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">
            Toplam {filteredCustomers.length} müşteri
          </p>
        </div>
        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700 text-white flex-1 sm:flex-none min-h-11">
                <Plus className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Yeni Müşteri</span>
                <span className="sm:hidden">Ekle</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-full sm:max-w-2xl max-h-[90vh] overflow-y-auto mx-4">
              <DialogHeader>
                <DialogTitle>Yeni Müşteri Ekle</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Müşteri Adı *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="text-base"
                  />
                </div>
                <div>
                  <Label>Telefon</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    type="tel"
                    className="text-base"
                  />
                </div>
                <div>
                  <Label>E-posta</Label>
                  <Input
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    type="email"
                    className="text-base"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Şehir</Label>
                    <Input
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      className="text-base"
                    />
                  </div>
                  <div>
                    <Label>İlçe</Label>
                    <Input
                      value={formData.district}
                      onChange={(e) => setFormData({ ...formData, district: e.target.value })}
                      className="text-base"
                    />
                  </div>
                </div>
                <div>
                  <Label>İletişim Kişisi</Label>
                  <Input
                    value={formData.contact_person}
                    onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                    className="text-base"
                  />
                </div>
                <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white min-h-11">
                  Müşteri Ekle
                </Button>
              </form>
            </DialogContent>
          </Dialog>

          <Button onClick={handleExport} variant="outline" className="flex-1 sm:flex-none min-h-11">
            <Download className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Excel</span>
          </Button>
        </div>
      </div>

      {/* Search and Filters - Geliştirilmiş */}
      <div className="bg-white rounded-xl shadow-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {/* Arama */}
          <div className="relative lg:col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              placeholder="Ad, telefon, şehir, yetkili ile ara..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
              }}
              className="pl-11 text-base"
              data-testid="search-customers-input"
            />
          </div>
          
          {/* Şehir Filtresi */}
          <div>
            <Select value={selectedCity} onValueChange={setSelectedCity}>
              <SelectTrigger className="text-base">
                <SelectValue placeholder="Şehir" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Şehirler</SelectItem>
                {uniqueCities.map(city => (
                  <SelectItem key={city} value={city}>
                    {city}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        
        {/* Sıralama - İkinci Satır */}
        <div className="mt-3">
          <Select value={sortBy} onValueChange={(newValue) => {
            const oldValue = sortBy;
            setSortBy(newValue);
            pushAction({
              undo: () => setSortBy(oldValue)
            });
          }}>
            <SelectTrigger className="text-base">
              <SelectValue placeholder="Sırala" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="name-asc">İsim (A-Z)</SelectItem>
              <SelectItem value="name-desc">İsim (Z-A)</SelectItem>
              <SelectItem value="city-asc">Şehir (A-Z)</SelectItem>
              <SelectItem value="city-desc">Şehir (Z-A)</SelectItem>
              <SelectItem value="representative-asc">Temsilci (A-Z)</SelectItem>
              <SelectItem value="representative-desc">Temsilci (Z-A)</SelectItem>
              <SelectItem value="arvento-desc">Cihaz Sayısı (Çok-Az)</SelectItem>
              <SelectItem value="arvento-asc">Cihaz Sayısı (Az-Çok)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-3">
        {paginatedCustomers.map((customer) => (
          <div 
            key={customer.id} 
            className="bg-white rounded-lg shadow-md p-4 border-l-4 border-[#193046] cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => navigate(`/customers/${customer.id}`)}
          >
            <div className="flex justify-between items-start mb-3">
              <div className="flex-1">
                <h3 className="text-lg font-bold text-gray-900 mb-1">{customer.name}</h3>
                {customer.contact_person && (
                  <div className="text-sm text-gray-600 mb-1">Yetkili: {customer.contact_person}</div>
                )}
                <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                  <User className="w-4 h-4" />
                  <span>{customer.representative || 'Atanmamış'}</span>
                </div>
              </div>
              <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-bold">
                {customer.arvento_count || 0}
              </div>
            </div>
            
            <div className="space-y-2 mb-3">
              {customer.phone && (
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <Phone className="w-4 h-4 text-gray-400" />
                  <a href={`tel:${customer.phone}`} className="hover:text-[#193046]" onClick={(e) => e.stopPropagation()}>{customer.phone}</a>
                </div>
              )}
              {customer.email && (
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <Mail className="w-4 h-4 text-gray-400" />
                  <a href={`mailto:${customer.email}`} className="hover:text-[#193046]" onClick={(e) => e.stopPropagation()}>{customer.email}</a>
                </div>
              )}
              {customer.city && (
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  <span>{customer.city}{customer.district ? ` / ${customer.district}` : ''}</span>
                </div>
              )}
            </div>

            {user?.role === 'admin' && (
              <div className="flex justify-end">
                <Button
                  variant="destructive"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteCustomer(customer.id);
                  }}
                  className="min-h-11 px-4"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full table-auto">
            <thead className="progo-gradient text-gray-800">
              <tr>
                <th className="px-3 py-3 text-left text-xs sm:text-sm whitespace-nowrap">Müşteri Adı</th>
                <th className="px-3 py-3 text-left text-xs sm:text-sm whitespace-nowrap">Yetkili</th>
                <th className="px-3 py-3 text-left text-xs sm:text-sm whitespace-nowrap">Telefon</th>
                <th className="px-3 py-3 text-left text-xs sm:text-sm whitespace-nowrap">Şehir</th>
                <th className="px-3 py-3 text-center text-xs sm:text-sm whitespace-nowrap">Cihaz Sayısı</th>
                <th className="px-3 py-3 text-center text-xs sm:text-sm whitespace-nowrap">İşlemler</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {paginatedCustomers.map((customer) => (
                <tr 
                  key={customer.id} 
                  className="hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => navigate(`/customers/${customer.id}`)}
                >
                  <td className="px-3 py-3 font-medium text-gray-900 text-xs sm:text-sm">{customer.name}</td>
                  <td className="px-3 py-3 text-gray-600 text-xs sm:text-sm">{customer.contact_person || '-'}</td>
                  <td className="px-3 py-3 text-gray-600 text-xs sm:text-sm">{customer.phone || '-'}</td>
                  <td className="px-3 py-3 text-gray-600 text-xs sm:text-sm">{customer.city || '-'}</td>
                  <td className="px-3 py-3 text-center">
                    <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium">
                      {customer.arvento_count || 0}
                    </span>
                  </td>
                  {user?.role === 'admin' && (
                    <td className="px-3 py-3 text-center">
                      <div className="flex justify-center gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteCustomer(customer.id);
                          }}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 min-h-9"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  )}
                  {user?.role !== 'admin' && <td></td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Desktop Pagination */}
        {filteredCustomers.length > itemsPerPage && (
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 px-4 py-3 bg-gray-50 border-t">
            <div className="text-xs sm:text-sm text-gray-700">
              Gösterilen: {(currentPage - 1) * itemsPerPage + 1} - {Math.min(currentPage * itemsPerPage, filteredCustomers.length)} / {filteredCustomers.length}
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                variant="outline"
                size="sm"
                className="min-h-9"
              >
                Önceki
              </Button>
              <span className="px-3 py-1 text-xs sm:text-sm">
                {currentPage} / {Math.ceil(filteredCustomers.length / itemsPerPage)}
              </span>
              <Button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === Math.ceil(filteredCustomers.length / itemsPerPage)}
                variant="outline"
                size="sm"
                className="min-h-9"
              >
                Sonraki
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Mobile Pagination */}
      {filteredCustomers.length > itemsPerPage && (
        <div className="md:hidden flex flex-col sm:flex-row justify-between items-center gap-4 bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-700">
            Gösterilen: {(currentPage - 1) * itemsPerPage + 1} - {Math.min(currentPage * itemsPerPage, filteredCustomers.length)} / {filteredCustomers.length}
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              variant="outline"
              size="sm"
              className="min-h-10"
            >
              Önceki
            </Button>
            <span className="px-4 py-2 text-sm font-medium">
              {currentPage} / {Math.ceil(filteredCustomers.length / itemsPerPage)}
            </span>
            <Button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === Math.ceil(filteredCustomers.length / itemsPerPage)}
              variant="outline"
              size="sm"
              className="min-h-10"
            >
              Sonraki
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Portfolio;